import { HEX_1D2939, HEX_212F41, HEX_F2F6FA } from '@tanium/colors';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { CircleIcon } from '@tanium/react-composed-icons';
import { FormRow, FormSection } from '@tanium/react-form';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { CloseIcon } from '@tanium/react-graphics';
import { Stack } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { defineColor } from '@tanium/react-theme-definition';
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik';
import get from 'lodash.get';
import isequal from 'lodash.isequal';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StringParam, useQueryParam } from 'use-query-params';
import IAccessTypes from 'common/IAccessTypes';
import RequestAccessSelectors from 'components/app/store/requestAccess/RequestAccessSelectors';
import RequestAccessSlice, { AccessTypeOption } from 'components/app/store/requestAccess/RequestAccessSlice';
import UserSelectors from 'components/app/store/UserSelectors';
import AwsRoleTargetAccessSelectors from 'components/pages/console/store/AwsRoleTargetAccessSelectors';
import AwsRoleTargetAccessSlice from 'components/pages/console/store/AwsRoleTargetAccessSlice';
import BastionTargetAccessSelectors from 'components/pages/console/store/BastionTargetAccessSelectors';
import HostTargetAccessSelectors from 'components/pages/console/store/HostTargetAccessSelectors';
import K8sRoleTargetAccessSelectors from 'components/pages/console/store/K8sRoleTargetAccessSelectors';
import {
  ACCESS_TYPES,
  CreateRequestForm,
  TARGET_ACCESS_ID_ERROR_MESSAGE,
} from 'components/pages/request/CreateRequestPage';
import AccessLevelConfigDisplay from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessLevelConfigDisplay';
import AWSRoleBasedAccess from 'components/pages/request/requestPartials/createRequestFieldsPartials/accessTypePartials/AWSRoleBasedAccess';
import BastionAccess, {
  BASTION_TYPE,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/accessTypePartials/BastionAccess';
import HostBasedAccess from 'components/pages/request/requestPartials/createRequestFieldsPartials/accessTypePartials/HostBasedAccess';
import KubernetesClusterAPIAccess, {
  API_ACCESS_TYPE,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/accessTypePartials/KubernetesAPIAccess';
import { MsEntraIDGroupAccess } from 'components/pages/request/requestPartials/createRequestFieldsPartials/accessTypePartials/MsEntraIDGroupAccess';
import { SelectedAccessTypeCard } from 'components/pages/request/requestPartials/createRequestFieldsPartials/accessTypePartials/SelectedAccessTypeCard';
import { TARGET_ID } from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { AwsRoleTargetAccess } from 'gql/AwsRoleTargetAccess';
import { BastionTargetAccess } from 'gql/BastionTargetAccess';
import { HostTargetAccess } from 'gql/HostTargetAccess';
import { K8sRoleTargetAccess } from 'gql/K8sRoleTargetAccess';
import { RequestAccessInput, RequestTargetType } from 'gql/Request';
import User from 'gql/User';
import HostTargetAccessSlice from 'components/pages/console/store/HostTargetAccessSlice';
import BastionTargetAccessSlice from 'components/pages/console/store/BastionTargetAccessSlice';
import K8sRoleTargetAccessSlice from 'components/pages/console/store/K8sRoleTargetAccessSlice';
import MsEntraIDGroupTargetAccessSlice from 'components/pages/console/store/MsEntraIDGroupTargetAccessSlice';
import { MsEntraIDGroupTargetAccess } from 'gql/MsEntraIDGroupTargetAccess';
import MsEntraIDGroupTargetAccessSelectors from 'components/pages/console/store/MsEntraIDGroupTargetAccessSelectors';
import { NAMESPACE, ROLE } from './accessTypePartials/KubernetesRoleAccess';

const EmptyStateBackground = defineColor([HEX_F2F6FA, HEX_212F41]);
const AccessTypeSelectorBorder = defineColor([HEX_1D2939, HEX_F2F6FA]);

export const ACCESS_TYPE = 'accessType';
export const TARGET_ACCESS_ID = 'targetAccessID';

export const AccessType = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formInputIdPrefix = 'create-request-';
  const formik = useFormikContext<CreateRequestForm>();
  const user: User | undefined = useSelector(UserSelectors.selectUser);
  const accessTypeOptions: { [key in IAccessTypes]?: AccessTypeOption } | undefined = useSelector(
    RequestAccessSelectors.selectAccessTypeOptions
  );
  const awsRoleTargetAccess: AwsRoleTargetAccess[] = useSelector(
    AwsRoleTargetAccessSelectors.selectAwsRoleTargetAccesses
  );
  const hostTargetAccess: HostTargetAccess[] = useSelector(HostTargetAccessSelectors.selectHostTargetAccesses);
  const k8sTargetAccessList: K8sRoleTargetAccess[] = useSelector(
    K8sRoleTargetAccessSelectors.selectK8sRoleTargetAccessList
  );
  const bastionTargetAccess: BastionTargetAccess[] = useSelector(
    BastionTargetAccessSelectors.selectBastionTargetAccesses
  );
  const msEntraIDGroupTargetAccess: MsEntraIDGroupTargetAccess[] = useSelector(
    MsEntraIDGroupTargetAccessSelectors.selectMsEntraIDGroupTargetAccesses
  );

  const [selectedAccessErrors, setSelectedAccessErrors] = useState('');
  const [selectedAccessType, setSelectedAccessType] = useState<IAccessTypes | undefined>();
  const [targetIdQuery] = useQueryParam(TARGET_ID, StringParam);
  const theme = useTheme();

  // Clearing Access type radio button if the targetId gets changed
  useEffect(() => {
    setSelectedAccessType(undefined);
  }, [targetIdQuery]);

  useEffect(() => {
    if (targetIdQuery) {
      const filter = {
        includeAllTargetGroups: true,
        targetOrTargetGroupID: targetIdQuery,
        userADGroups: user?.roles || [],
      };
      dispatch(AwsRoleTargetAccessSlice.actions.listAwsRoleTargetAccessForTargetOrGroup(filter));
      dispatch(HostTargetAccessSlice.actions.listHostTargetAccessForTargetOrGroup(filter));
      dispatch(BastionTargetAccessSlice.actions.listBastionTargetAccessForTargetOrGroup(filter));
      dispatch(K8sRoleTargetAccessSlice.actions.listK8sRoleTargetAccess({ ...filter, summaryOnly: true }));
      dispatch(MsEntraIDGroupTargetAccessSlice.actions.listMsEntraIDGroupTargetAccessForTargetOrGroup(filter));
    }
  }, [targetIdQuery, user, dispatch]);

  useEffect(() => {
    if (awsRoleTargetAccess?.length > 0) {
      dispatch(RequestAccessSlice.actions.enableOption(IAccessTypes.AWS_ROLE));
    } else {
      dispatch(RequestAccessSlice.actions.disableOption(IAccessTypes.AWS_ROLE));
    }
    if (hostTargetAccess?.length > 0) {
      dispatch(RequestAccessSlice.actions.enableOption(IAccessTypes.HOST));
    } else {
      dispatch(RequestAccessSlice.actions.disableOption(IAccessTypes.HOST));
    }
    if (bastionTargetAccess?.length > 0) {
      dispatch(RequestAccessSlice.actions.enableOption(IAccessTypes.BASTION));
    } else {
      dispatch(RequestAccessSlice.actions.disableOption(IAccessTypes.BASTION));
    }
    if (k8sTargetAccessList?.length > 0) {
      dispatch(RequestAccessSlice.actions.enableOption(IAccessTypes.K8S_API));
    } else {
      dispatch(RequestAccessSlice.actions.disableOption(IAccessTypes.K8S_API));
    }
    if (msEntraIDGroupTargetAccess?.length > 0) {
      dispatch(RequestAccessSlice.actions.enableOption(IAccessTypes.MS_ENTRA_ID_GROUP));
    } else {
      dispatch(RequestAccessSlice.actions.disableOption(IAccessTypes.MS_ENTRA_ID_GROUP));
    }
  }, [
    awsRoleTargetAccess,
    hostTargetAccess,
    bastionTargetAccess,
    k8sTargetAccessList,
    accessTypeOptions,
    msEntraIDGroupTargetAccess,
    dispatch,
  ]);

  const onChangeClicked = () => {
    // Clear values
    void formik.setFieldValue(TARGET_ACCESS_ID, undefined);
    void formik.setFieldTouched(TARGET_ACCESS_ID, false);

    setSelectedAccessErrors('');
  };

  const clearState = () => {
    setSelectedAccessType(undefined);
    setSelectedAccessErrors('');
    onChangeClicked();
  };

  // When an accessType gets added to the selected list
  const onAddAccessTypeClicked = () => {
    const { values } = formik;
    let validated = true;

    const accessObjToAdd: RequestAccessInput = {
      type: values[ACCESS_TYPE],
      targetAccessID: values[TARGET_ACCESS_ID],
      namespace: values[NAMESPACE],
      role: values[ROLE],
      apiAccessType: values[API_ACCESS_TYPE],
    };
    if (
      (values[ACCESS_TYPE] === IAccessTypes.AWS_ROLE ||
        values[ACCESS_TYPE] === IAccessTypes.MS_ENTRA_ID_GROUP ||
        values[ACCESS_TYPE] === IAccessTypes.BASTION ||
        values[ACCESS_TYPE] === IAccessTypes.HOST ||
        values[ACCESS_TYPE] === IAccessTypes.K8S_API) &&
      !validateTargetAccessID()
    ) {
      validated = false;
    }
    if (isDuplicate(accessObjToAdd)) {
      validated = false;
    }
    if (values[ACCESS_TYPE] && validated) {
      void formik.setFieldValue(ACCESS_TYPES, [...values[ACCESS_TYPES], accessObjToAdd]);
      void formik.setFieldValue(ACCESS_TYPE, undefined);
      clearState();
    }
  };

  const isDuplicate = (accessObj: RequestAccessInput): boolean => {
    const { values } = formik;
    const foundDuplicate = values[ACCESS_TYPES].some((acc: RequestAccessInput) => isAccessEqual(acc, accessObj));
    if (foundDuplicate) {
      setSelectedAccessErrors(t('DUPLICATE_ACCESS_TYPE'));
    }
    return foundDuplicate;
  };

  const isAccessEqual = (value: RequestAccessInput, other: RequestAccessInput): boolean => {
    switch (value.type) {
      case IAccessTypes.BASTION:
        return other.type === IAccessTypes.BASTION;
      default:
        return isequal(value, other);
    }
  };

  const validateTargetAccessID = (): boolean => {
    const { values } = formik;
    if (!values[TARGET_ACCESS_ID]) {
      formik.setFieldError(
        `${formInputIdPrefix}${TARGET_ACCESS_ID}`,
        TARGET_ACCESS_ID_ERROR_MESSAGE as unknown as string
      );
      return false;
    }
    return true;
  };

  const removeAccessType = (id: number) => {
    const accessTypesArr = formik.values[ACCESS_TYPES];
    void formik.setFieldValue(
      ACCESS_TYPES,
      accessTypesArr.filter((_: RequestAccessInput, i: number) => i !== id)
    );
  };

  const accessTypeOptionsArr = useCallback(() => {
    if (accessTypeOptions) {
      return Object.keys(accessTypeOptions)
        .sort()
        .map((key) => {
          const option = accessTypeOptions[key as IAccessTypes]!;
          return {
            label: t(key),
            value: option.value,
            description: t(`${option.value}_INFO`),
            isDisabled: option.disabledThroughRbac || option.disabledThroughUi,
          };
        });
    }
    return [];
  }, [t, accessTypeOptions]);

  return (
    <FormSection title={t('SELECT_ACCESS_TYPE')}>
      <AccessLevelConfigDisplay />
      {formik?.values.accessTypes?.length ? (
        <Box display="flex" flexDirection="column">
          {formik.values[ACCESS_TYPES].map((acc, i) => {
            return (
              <SelectedAccessTypeCard id={i} key={i} {...acc} onDeleteClicked={(id: number) => removeAccessType(id)} />
            );
          })}
        </Box>
      ) : (
        <Box
          backgroundColor={EmptyStateBackground(theme)}
          color={theme.colors.subtle}
          borderTopRadius="0.2em"
          p={2}
          justifyContent="center"
          display="flex"
        >
          <strong>{t('NO_ACCESS_TYPES_SELECTED')}</strong>
        </Box>
      )}

      <Box>
        <Box
          display="flex"
          border={1}
          py={1}
          pl={2}
          borderBottomRadius={'0.2em'}
          flexWrap="wrap"
          borderColor={AccessTypeSelectorBorder(theme)}
        >
          <Box mr={1} data-testid="access-type-select-input">
            <Field name={ACCESS_TYPE}>
              {({ field, form }: FieldProps<any, CreateRequestForm>): React.ReactElement => (
                <FormRow
                  label={t('ACCESS_TYPE')}
                  description={t('ACCESS_TYPE_INFO')}
                  errors={
                    ((get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>) ||
                    ((get(form.touched, ACCESS_TYPES) && get(form.errors, ACCESS_TYPES)) as FormikErrors<string>) ||
                    selectedAccessErrors
                  }
                  labelId={`${formInputIdPrefix}${field.name}`}
                  required
                >
                  <FormSelectInput
                    onChange={(option) => {
                      const { value } = option;
                      void formik.setFieldValue(ACCESS_TYPE, value);
                      setSelectedAccessType(value);
                      onChangeClicked();
                    }}
                    value={accessTypeOptionsArr().find((option) => option.value === field.value) || ''}
                    inputId={`${formInputIdPrefix}${field.name}`}
                    options={accessTypeOptionsArr()}
                    aria-label={t('ACCESS_TYPE')}
                    formatOptionLabel={(
                      option: {
                        value: number;
                        label: string;
                        description: string;
                      },
                      state
                    ) => {
                      const isSelected =
                        state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                      /* istanbul ignore next */
                      const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                      return (
                        <Stack direction="column" itemSpacing={0.5}>
                          <Box display="inline-flex">
                            <Box mr={1}>{option.label}</Box>
                          </Box>
                          <Box color={subtextColor} fontSize={11} display="flex" flexWrap={'wrap'}>
                            {option.description}
                          </Box>
                        </Stack>
                      );
                    }}
                    styles={{
                      singleValue: (base) => ({
                        ...base,
                        marginTop: theme.space[1],
                        marginBottom: theme.space[1],
                        position: 'relative',
                        transform: 'none',
                        whiteSpace: 'normal',
                      }),
                      input: (base) => ({
                        ...base,
                        position: 'absolute',
                        top: theme.space[1] - 2,
                        paddingTop: 0,
                      }),
                    }}
                  />
                </FormRow>
              )}
            </Field>
          </Box>
          {selectedAccessType === IAccessTypes.HOST && <HostBasedAccess hostTargetAccess={hostTargetAccess} />}
          {selectedAccessType === IAccessTypes.AWS_ROLE && (
            <AWSRoleBasedAccess awsRoleTargetAccess={awsRoleTargetAccess} />
          )}
          {selectedAccessType === IAccessTypes.BASTION && <BastionAccess bastionTargetAccess={bastionTargetAccess} />}
          {selectedAccessType === IAccessTypes.K8S_API &&
            (formik.values.targetType === RequestTargetType.KubernetesCluster ||
              formik.values.targetType === RequestTargetType.CustomerEnvironment ||
              formik.values.targetType === RequestTargetType.EosService) && (
              <KubernetesClusterAPIAccess k8sTargetAccessList={k8sTargetAccessList} />
            )}
          {selectedAccessType === IAccessTypes.MS_ENTRA_ID_GROUP && (
            <MsEntraIDGroupAccess msEntraIDGroupTargetAccess={msEntraIDGroupTargetAccess} />
          )}
          <Box m={2} alignSelf="center" flexGrow={1} display="flex" justifyContent="end">
            <Button
              data-testid="access-type-apply-button"
              variant="primary"
              type="button"
              onClick={async () => {
                void formik.setFieldTouched(ACCESS_TYPE, true);
                void formik.setFieldTouched(ROLE, true);
                void formik.setFieldTouched(BASTION_TYPE, true);
                void formik.setFieldTouched(API_ACCESS_TYPE, true);
                void formik.setFieldTouched(NAMESPACE, true);
                void formik.setFieldTouched(TARGET_ACCESS_ID, true);
                await formik.validateForm();
                onAddAccessTypeClicked();
              }}
            >
              {t('APPLY')}
            </Button>
            <Box ml={1}>
              <Button
                data-testid="access-type-clear-button"
                type="button"
                onClick={() => {
                  void formik.setFieldValue(ACCESS_TYPE, undefined);
                  clearState();
                }}
              >
                <CircleIcon outerRadius={5} innerRadius={7} backgroundColor={'rgba(0,0,0,0)'} icon={CloseIcon} />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </FormSection>
  );
};

export default AccessType;
